import { Measure } from '@cbsi-cbscom/measure';
import { getLanguageLabelByCode, Language } from 'library/ts/model/language/Language';
import { checkAutoplay } from 'library/ts/utilities/Autoplay';
import { Cookies } from 'library/ts/utilities/Cookies';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { ConnectionMonitor } from 'library/ts/utilities/network/ConnectionMonitor';
import { PublicEnums } from 'tag/ts/smart_tag/model/enums/PublicEnums';
import { executeSmartTagReadyPageCallback } from 'tag/ts/smart_tag/utilities/Callback';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { AviaAdapter } from 'tag/ts/tag/player/adapters/avia/AviaAdapter';
import { PlayerAdapterType } from 'tag/ts/tag/player/enums/PlayerAdapterType';
import { Tag } from 'tag/ts/tag/Tag';
export class SmartTag {
    #enums;
    #connectionMonitor;
    #DETECT_AUTOPLAY = true;
    #autoplay;
    deviceCapabilities;
    #runtimeVO;
    #logger;
    #registry = {};
    #list = [];
    constructor(para = window?.PARA) {
        this.#enums = new PublicEnums();
        this.#logger = LogService.create({ prefixes: ['SmartTag:'] });
        this.utilities.Delay.delay().then(() => {
            this.#runtimeVO = getSmartTagRuntimeVO();
            this.#logger.log('this.#runtimeVO?.readyCallback?.detectAutoplay', this.#runtimeVO?.readyCallback?.detectAutoplay);
            if (!this.#runtimeVO?.readyCallback?.detectAutoplay) {
                this.#DETECT_AUTOPLAY = false;
            }
            this.#start();
        });
    }
    get name() {
        return 'SmartTag';
    }
    get info() {
        return {
            version: window.PlayerServicesProjectInfo.release_version,
            date: window.PlayerServicesProjectInfo.app_compile_time.tag.token
        };
    }
    get startTime() {
        return window?.PARA?.PlayerServices?.StartTime;
    }
    get utilities() {
        return window?.PARA?.PlayerServices?.Utilities;
    }
    get cookies() {
        return Cookies.getInstance();
    }
    get capabilities() {
        return window?.PARA?.PlayerServices?.Capabilities;
    }
    get online() {
        return this.#connectionMonitor !== undefined
            ? this.#connectionMonitor.onLine
            : undefined;
    }
    get autoplay() {
        return this.#autoplay;
    }
    get runtimeVO() {
        return this.#runtimeVO;
    }
    get registry() {
        return this.#registry;
    }
    set registry(value) {
        this.#registry = value;
    }
    get list() {
        return this.#list;
    }
    set list(value) {
        this.#list = value;
    }
    get enums() {
        return this.#enums;
    }
    get Language() {
        return {
            list: Language,
            getLanguageLabelByCode: getLanguageLabelByCode
        };
    }
    get playerAdapters() {
        return {
            [PlayerAdapterType.AVIA]: AviaAdapter
        };
    }
    get performanceMetrics() {
        return Measure?.metrics;
    }
    create = (paramsVO, callbacksVO = {}) => {
        this.#logger.log('create:', paramsVO);
        Measure.end(Metrics.importToCreateTime);
        Measure.start(Metrics.createToLoadTime);
        return new Promise((onReadyResolve, onReadyReject) => {
            const tag = new Tag({
                paramsVO: paramsVO,
                callbacksVO: callbacksVO,
                resolve: onReadyResolve,
                reject: onReadyReject
            });
            this.registry[tag.uuid] = tag;
            if (this.utilities.Array.hasObjWithPropValue(this.list, 'uuid', tag.uuid) === false) {
                this.list.push(tag);
            }
        });
    };
    remove = (tag) => {
        this.#logger.log('remove:', tag);
        return new Promise((resolve, reject) => {
            if (this.utilities.Type.isUndefined(tag))
                reject({
                    msg: 'Failure: Tag instance is undefined. Request to remove rejected.'
                });
            tag.API.TAG.reset().then((data) => {
                this.#logger.log('remove:', 'Tag.reset():', data, 'Done!');
                const indexToDelete = this.utilities.Array.indexOf('uuid', tag.uuid, this.list);
                if (indexToDelete !== null) {
                    this.list.splice(indexToDelete, 1);
                    delete this.registry[tag.uuid];
                    tag = undefined;
                }
                this.utilities.Delay.delay().then(() => {
                    this.#logger.log('remove:', 'inform the app!');
                    resolve({ msg: 'Success: tag was removed.' });
                });
            });
        });
    };
    reset = () => {
        return new Promise((resolve, reject) => {
            Object.keys(this.registry).forEach((key) => {
                let tag = this.registry[key];
                tag.API.TAG.reset().then((data) => {
                    this.#logger.log('reset:', 'Tag.reset():', data);
                    const indexToDelete = this.utilities.Array.indexOf('uuid', tag.uuid, this.list);
                    if (indexToDelete !== null) {
                        this.list.splice(indexToDelete, 1);
                        delete this.registry[key];
                        tag = undefined;
                    }
                });
            });
            this.list = [];
            this.registry = {};
            setTimeout(() => {
                resolve({ msg: 'Success: all tags removed.' });
            }, 100);
        });
    };
    #start = () => {
        this.#logger.log('Start');
        this.#connectionMonitor = new ConnectionMonitor(this.utilities, this.capabilities, this.#connectionManagerCallback);
        switch (this.#DETECT_AUTOPLAY) {
            case true:
                checkAutoplay(this.capabilities)
                    .then((autoplayInfo) => {
                    this.#logger.log('checkAutoplay SUCCESS:', autoplayInfo);
                    this.#autoplay = autoplayInfo;
                    setTimeout(() => {
                        executeSmartTagReadyPageCallback(this);
                    });
                })
                    .catch((error) => {
                    this.#logger.warn('checkAutoplay FAILURE:', error);
                });
                break;
            case false:
                setTimeout(() => {
                    this.#logger.log('checkAutoplay SKIPPED: this.#DETECT_AUTOPLAY=', this.#DETECT_AUTOPLAY);
                    executeSmartTagReadyPageCallback(this);
                });
                break;
        }
    };
    #connectionManagerCallback = (online) => {
        Object.keys(this.registry).forEach((key) => {
            this.registry[key].dispatchTagEvent(this.registry[key].getTagEventVO(online === true ? TagEvent.ON_ONLINE : TagEvent.ON_OFFLINE, this.registry[key], {
                online: online
            }, this, this.name));
        });
    };
}
